import { useState, useRef, useCallback } from 'react';
import { Download, X, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const STYLES = [
  {
    id: 'urban',
    label: 'Urban',
    bg: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    textColor: '#f0f0f0',
    accentColor: '#e94560',
    quoteColor: '#ffffff',
    font: "'Georgia', serif",
    labelFont: "'Arial', sans-serif",
  },
  {
    id: 'minimal',
    label: 'Minimal',
    bg: 'linear-gradient(180deg, #fafafa 0%, #f5f5f5 100%)',
    textColor: '#333333',
    accentColor: '#1d4ed8',
    quoteColor: '#111111',
    font: "'Georgia', serif",
    labelFont: "'Helvetica', sans-serif",
  },
  {
    id: 'warm',
    label: 'Warm',
    bg: 'linear-gradient(135deg, #fdf6e3 0%, #f5e6c8 50%, #ecd5a0 100%)',
    textColor: '#5c4a32',
    accentColor: '#c17f24',
    quoteColor: '#3a2f1f',
    font: "'Georgia', serif",
    labelFont: "'Arial', sans-serif",
  },
  {
    id: 'nature',
    label: 'Natur',
    bg: 'linear-gradient(135deg, #1b3a2d 0%, #2d5a3f 50%, #3a7a52 100%)',
    textColor: '#d4e8d0',
    accentColor: '#8fbc8f',
    quoteColor: '#ffffff',
    font: "'Georgia', serif",
    labelFont: "'Arial', sans-serif",
  },
];

function wrapText(ctx, text, maxWidth) {
  const words = text.split(' ');
  const lines = [];
  let line = '';
  for (const word of words) {
    const testLine = line ? `${line} ${word}` : word;
    if (ctx.measureText(testLine).width > maxWidth && line) {
      lines.push(line);
      line = word;
    } else {
      line = testLine;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function drawCarmaImage(canvas, carma, style) {
  const ctx = canvas.getContext('2d');
  const W = 1080;
  const H = 1080;
  canvas.width = W;
  canvas.height = H;

  // Background
  const grad = ctx.createLinearGradient(0, 0, W, H);
  if (style.id === 'urban') {
    grad.addColorStop(0, '#1a1a2e');
    grad.addColorStop(0.5, '#16213e');
    grad.addColorStop(1, '#0f3460');
  } else if (style.id === 'minimal') {
    grad.addColorStop(0, '#fafafa');
    grad.addColorStop(1, '#f0f0f0');
  } else if (style.id === 'warm') {
    grad.addColorStop(0, '#fdf6e3');
    grad.addColorStop(0.5, '#f5e6c8');
    grad.addColorStop(1, '#ecd5a0');
  } else {
    grad.addColorStop(0, '#1b3a2d');
    grad.addColorStop(0.5, '#2d5a3f');
    grad.addColorStop(1, '#3a7a52');
  }
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);

  // Subtle texture dots
  ctx.globalAlpha = 0.03;
  for (let i = 0; i < 800; i++) {
    ctx.fillStyle = style.id === 'minimal' || style.id === 'warm' ? '#000' : '#fff';
    ctx.beginPath();
    ctx.arc(Math.random() * W, Math.random() * H, Math.random() * 2, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // "WERTSCHÄTZUNG" label
  ctx.font = `bold 13px ${style.labelFont}`;
  ctx.fillStyle = style.accentColor;
  ctx.letterSpacing = '6px';
  ctx.textAlign = 'center';
  ctx.fillText('W E R T S C H Ä T Z U N G', W / 2, 120);

  // Accent line
  ctx.strokeStyle = style.accentColor;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 60, 140);
  ctx.lineTo(W / 2 + 60, 140);
  ctx.stroke();

  // Quote mark
  ctx.font = `bold 120px ${style.font}`;
  ctx.fillStyle = style.accentColor;
  ctx.globalAlpha = 0.15;
  ctx.textAlign = 'left';
  ctx.fillText('\u201C', 80, 260);
  ctx.globalAlpha = 1;

  // Message
  const maxLineWidth = W - 180;
  const fontSize = carma.message.length > 300 ? 26 : carma.message.length > 150 ? 30 : 36;
  ctx.font = `italic ${fontSize}px ${style.font}`;
  ctx.fillStyle = style.quoteColor;
  ctx.textAlign = 'left';

  const lines = wrapText(ctx, carma.message, maxLineWidth);
  const lineHeight = fontSize * 1.5;
  const startY = 300;
  const maxLines = Math.min(lines.length, 12);

  for (let i = 0; i < maxLines; i++) {
    let text = lines[i];
    if (i === maxLines - 1 && lines.length > maxLines) text += ' ...';
    ctx.fillText(text, 90, startY + i * lineHeight);
  }

  // Context (if any)
  const afterQuoteY = startY + maxLines * lineHeight + 30;
  if (carma.context) {
    ctx.font = `14px ${style.labelFont}`;
    ctx.fillStyle = style.textColor;
    ctx.globalAlpha = 0.6;
    ctx.fillText(carma.context, 90, afterQuoteY);
    ctx.globalAlpha = 1;
  }

  // Sender info
  const senderY = H - 200;
  ctx.font = `bold 18px ${style.labelFont}`;
  ctx.fillStyle = style.textColor;
  ctx.textAlign = 'left';
  ctx.fillText(`Von: ${carma.senderName}`, 90, senderY);

  if (carma.receiverName) {
    ctx.font = `18px ${style.labelFont}`;
    ctx.fillText(`An: ${carma.receiverName}`, 90, senderY + 30);
  }

  // Date
  const dateStr = new Date(carma.createdAt).toLocaleDateString('de-DE', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
  ctx.font = `13px ${style.labelFont}`;
  ctx.fillStyle = style.textColor;
  ctx.globalAlpha = 0.5;
  ctx.fillText(dateStr, 90, senderY + 65);
  ctx.globalAlpha = 1;

  // Bottom branding
  ctx.font = `bold 14px ${style.labelFont}`;
  ctx.fillStyle = style.accentColor;
  ctx.textAlign = 'center';
  ctx.fillText('CarmaCircle', W / 2, H - 60);
  ctx.font = `11px ${style.labelFont}`;
  ctx.fillStyle = style.textColor;
  ctx.globalAlpha = 0.4;
  ctx.fillText('carmacircle.org', W / 2, H - 40);
  ctx.globalAlpha = 1;
}

export default function CarmaImageModal({ carma, onClose }) {
  const { t } = useTranslation();
  const [selectedStyle, setSelectedStyle] = useState(STYLES[0]);
  const canvasRef = useRef(null);
  const [rendered, setRendered] = useState(false);

  const renderCanvas = useCallback((style) => {
    if (!canvasRef.current || !carma) return;
    drawCarmaImage(canvasRef.current, carma, style);
    setRendered(true);
  }, [carma]);

  const selectStyle = (style) => {
    setSelectedStyle(style);
    renderCanvas(style);
  };

  // Render on first mount
  const canvasRefCallback = useCallback((node) => {
    canvasRef.current = node;
    if (node && carma) {
      drawCarmaImage(node, carma, selectedStyle);
      setRendered(true);
    }
  }, [carma, selectedStyle]);

  const downloadImage = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `carma-${Date.now()}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
  };

  const shareImage = async () => {
    if (!canvasRef.current || !navigator.share) return;
    try {
      const blob = await new Promise(resolve => canvasRef.current.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'carma.png', { type: 'image/png' });
      await navigator.share({
        title: 'Carma von CarmaCircle',
        text: 'Eine Wertschätzung, die mich berührt hat.',
        files: [file],
      });
    } catch { /* user cancelled share dialog */ }
  };

  if (!carma) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="carma-image-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <ImageIcon size={18} className="text-[var(--primary)]" />
            <h3 className="text-sm font-semibold">{t('imageModal.title')}</h3>
          </div>
          <button onClick={onClose} className="text-[var(--muted)] hover:text-[var(--text)]" data-testid="close-image-modal">
            <X size={18} />
          </button>
        </div>

        {/* Style selector */}
        <div className="p-4 border-b border-[var(--border)]">
          <p className="text-xs text-[var(--muted)] mb-2">{t('imageModal.styleLabel')}</p>
          <div className="flex gap-2">
            {STYLES.map((s) => (
              <button
                key={s.id}
                onClick={() => selectStyle(s)}
                data-testid={`style-${s.id}-btn`}
                className={`flex-1 rounded-lg px-3 py-2 text-xs font-medium border transition-colors ${
                  selectedStyle.id === s.id
                    ? 'border-[var(--primary)] bg-[var(--primary)]/10 text-[var(--primary)]'
                    : 'border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:border-[var(--primary)]/30'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Canvas preview */}
        <div className="p-4">
          <canvas
            ref={canvasRefCallback}
            className="w-full rounded-lg shadow-sm"
            style={{ aspectRatio: '1/1' }}
          />
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-[var(--border)] flex gap-2">
          <button
            onClick={downloadImage}
            disabled={!rendered}
            data-testid="download-image-btn"
            className="flex-1 rounded-full bg-[var(--primary)] text-[var(--primary-contrast)] py-2.5 text-sm font-medium hover:opacity-90 disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            <Download size={14} /> {t('imageModal.download')}
          </button>
          {typeof navigator !== 'undefined' && navigator.share && (
            <button
              onClick={shareImage}
              disabled={!rendered}
              data-testid="share-image-btn"
              className="flex-1 rounded-full border border-[var(--border)] bg-[var(--surface)] py-2.5 text-sm font-medium hover:bg-[var(--bg)] inline-flex items-center justify-center gap-2"
            >
              {t('imageModal.share')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
